import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Calendar, User, Briefcase, Loader2, AlertCircle, ChevronRight, ShieldCheck, MapPin, Star, Sparkles, X } from 'lucide-react';
import { REQUIRED_FIELD_LABELS } from '../../lib/idLabels';
import { wallTime, localDateStr, localInputValue } from '../../lib/time';
import { DateDropdownPicker } from '../../components/DateDropdownPicker';
import { CurrencyPreview } from '../../components/CurrencyPreview';

/**
 * Patient self-booking. Requires completed intake on first visit.
 */
export default function PatientBook() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectClinician = searchParams.get('clinician');
  const [services, setServices] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [locations, setLocations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [apptCount, setApptCount] = useState(0);
  const [hasIntake, setHasIntake] = useState(null);

  const [serviceId, setServiceId] = useState('');
  const [discipline, setDiscipline] = useState('');  // physio | osteopathy | chiropractic (Wave 2)
  const [clinicianId, setClinicianId] = useState(preselectClinician || '');
  const [locationId, setLocationId] = useState('');
  const [bookDate, setBookDate] = useState('');     // 'YYYY-MM-DD'
  const [startsAt, setStartsAt] = useState('');     // ISO chosen slot
  const [freeSlots, setFreeSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [completedCount, setCompletedCount] = useState(0);
  const [reason, setReason] = useState('');
  const [newCondition, setNewCondition] = useState(false);
  const [waitlistJoining, setWaitlistJoining] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [completeness, setCompleteness] = useState(null);
  // Iter 72 — massage intake (SOAP Part 1) gate.
  const [massageIntakeStatus, setMassageIntakeStatus] = useState(null);
  const [showMassageGate, setShowMassageGate] = useState(false);
  // Iter 130 — Pre-population banner. Names the sources we used so the
  // patient knows why fields are filled + can wipe them in a single tap.
  const [prefillSummary, setPrefillSummary] = useState(null);

  const loadCompleteness = () => api.get('/patients/me/completeness').then(r => setCompleteness(r.data)).catch(()=>{});

  // Iter 130 — Reset every user-editable field to blank state. Called by the
  // "Start blank" chip on the pre-fill banner.
  const clearPrefill = () => {
    if (!preselectClinician) setClinicianId('');
    setServiceId('');
    // Only clear discipline if the clinic offers >1 (single-discipline tenants
    // auto-select their only option — never wipe that).
    if ((settings?.service_disciplines || ['physiotherapy']).length > 1) {
      setDiscipline('');
    }
    setReason('');
    setNotes('');
    setBookDate('');
    setStartsAt('');
    setPrefillSummary(null);
    try { sessionStorage.removeItem('wizard_answers'); } catch (_) { /* ignore */ }
  };

  const joinWaitlist = async () => {
    setWaitlistJoining(true);
    try {
      const me = await api.get('/auth/me').then(r => r.data);
      await api.post('/waitlist', {
        patient_id: me.user_id,
        clinician_id: clinicianId || null,
        service_id: serviceId || null,
        preferred_window: bookDate || null,
        notes: notes || null,
      });
      setWaitlistJoined(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not join waitlist.');
    } finally { setWaitlistJoining(false); }
  };

  useEffect(() => {
    // Iter 130 — Public booking wizard answers (from BookingWizard.jsx). They
    // are set on the marketing site before signup and carried across. We only
    // consume them ONCE per booking attempt (removed after prefill so a
    // second booking session doesn't reuse stale answers).
    let wizardAns = null;
    try {
      const raw = sessionStorage.getItem('wizard_answers');
      if (raw) wizardAns = JSON.parse(raw);
    } catch (_) { /* ignore */ }

    // Iter 130 — Load appointments AFTER services + clinicians so we can
    // validate the seed IDs still exist in the active lists (and skip any
    // that were deleted / deactivated). Chain the calls so the seed uses
    // fresh data.
    Promise.all([
      api.get('/services').then(r => r.data.filter(s => s.active)),
      api.get('/clinicians').then(r => r.data),
      api.get('/appointments').then(r => r.data || []),
    ]).then(([activeSvcs, activeClins, appts]) => {
      setApptCount(appts.length);
      setCompletedCount(appts.filter(a => a.status === 'completed').length);

      const activeSvcIds = new Set(activeSvcs.map(s => s.service_id));
      const activeClinIds = new Set(activeClins.map(c => c.user_id));

      // Iter 130 — Pre-populate booking form from the patient's most useful
      // prior appointment. Returning patients almost always rebook the same
      // service + same clinician + same branch, so making them re-pick every
      // field is pure friction. Date/time are NEVER pre-selected — they're
      // always a fresh choice.
      //
      // "Useful" = has service_id AND clinician_id (both still active). We
      // fall back to any non-cancelled appointment if there's no fully-loaded
      // one, so first-time-rebook cases still get something helpful.
      const eligible = appts.filter(a => (
        a.status !== 'cancelled' && a.status !== 'no_show'
      ));
      const scoreSeed = (a) => {
        let s = 0;
        if (a.service_id && activeSvcIds.has(a.service_id)) s += 2;
        if (a.clinician_id && activeClinIds.has(a.clinician_id)) s += 2;
        if (a.discipline) s += 1;
        return s;
      };
      // Best appointment = highest data score, tiebreak by recency.
      const ranked = [...eligible].sort((a, b) => {
        const ds = scoreSeed(b) - scoreSeed(a);
        if (ds !== 0) return ds;
        return (b.starts_at || '').localeCompare(a.starts_at || '');
      });
      const seed = ranked[0];

      const filledFrom = [];
      if (seed) {
        if (!preselectClinician && seed.clinician_id && activeClinIds.has(seed.clinician_id)) {
          setClinicianId(seed.clinician_id);
          filledFrom.push('clinician');
        }
        if (seed.service_id && activeSvcIds.has(seed.service_id)) {
          setServiceId(seed.service_id);
          const svc = activeSvcs.find(s => s.service_id === seed.service_id);
          if (svc?.currency) setCurrency(svc.currency);
          filledFrom.push('service');
        }
        if (seed.location_id) {
          setLocationId(seed.location_id);
          filledFrom.push('location');
        }
        if (seed.discipline) {
          setDiscipline(seed.discipline);
          if (!filledFrom.includes('discipline')) filledFrom.push('discipline');
        }
      }
      // Wizard complaint → default reason-for-visit textarea for returning
      // patients. First-timers use the intake form instead so we skip.
      if (wizardAns?.complaint) {
        setReason(wizardAns.complaint);
        filledFrom.push('reason');
      }
      if (filledFrom.length > 0) {
        setPrefillSummary({
          fields: filledFrom,
          source: seed ? 'last_visit' : 'wizard',
          seedDate: seed?.starts_at || null,
        });
      }
      // Iter 130 — clean up wizard answers ONCE they've been consumed so a
      // 2nd booking attempt in the same session doesn't reuse them.
      try {
        if (wizardAns) sessionStorage.removeItem('wizard_answers');
      } catch (_) { /* ignore */ }

      // Populate the visible service + clinician dropdowns.
      setServices(activeSvcs);
      setClinicians(activeClins);
      // If the deep-link clinician is no longer active, ignore the preselect.
      if (preselectClinician && !activeClinIds.has(preselectClinician)) {
        setClinicianId('');
      }
    }).catch(() => {
      // Fallback — always populate services/clinicians even if the seed
      // pipeline blew up.
      api.get('/services').then(r => setServices(r.data.filter(s => s.active)));
      api.get('/clinicians').then(r => setClinicians(r.data));
    });
    api.get('/clinic/settings').then(r => {
      setSettings(r.data);
      // Iter 136 — auto-select using the VISIBLE discipline list (massage is
      // filtered out of this flow), matching the picker + submit validation.
      const list = (r.data?.service_disciplines || ['physiotherapy']).filter(d => d !== 'massage_therapy');
      if (list.length === 1) {
        setDiscipline(list[0]);
      } else if (wizardAns?.discipline && list.includes(wizardAns.discipline)) {
        // Iter 130 — Public funnel picked a discipline — honour it if the
        // clinic still supports it. `unsure` is ignored (no valid mapping).
        setDiscipline(wizardAns.discipline);
      }
    });
    // Branch picker — only shown when the tenant has 2+ active locations.
    // Auto-pick the primary (or first) so single-location tenants stay 1-click.
    api.get('/clinic/locations/public')
      .then(r => {
        const locs = r.data || [];
        setLocations(locs);
        if (locs.length > 0) {
          const primary = locs.find(l => l.is_primary) || locs[0];
          setLocationId(primary.location_id);
        }
      })
      .catch(() => setLocations([]));

    api.get('/interviews/me').then(r => setHasIntake(!!r.data?.chief_complaint));
    // Iter 72 — load massage intake status so we can pop the gate when the
    // patient picks the massage discipline.
    api.get('/massage/intake/me/status').then(r => setMassageIntakeStatus(r.data)).catch(() => setMassageIntakeStatus({ complete: false, has_subjective: false }));
    loadCompleteness();
  }, [preselectClinician]);

  const selectedService = services.find(s => s.service_id === serviceId);
  const isFirstVisit = completedCount === 0;       // never completed a session
  const needsIntake = isFirstVisit && !hasIntake;   // first-timer w/o intake → MUST complete
  const isReturning = !isFirstVisit;
  const prepayPercent = settings?.prepay_percent ?? 50;

  // Fetch real free slots whenever clinician + date + service are all chosen
  useEffect(() => {
    setStartsAt('');
    if (!clinicianId || !bookDate) { setFreeSlots([]); return; }
    const duration = selectedService?.duration_min || 45;
    setSlotsLoading(true);
    api.get(`/clinicians/${clinicianId}/free-slots`, { params: { date: bookDate, duration } })
       .then(r => setFreeSlots(r.data))
       .catch(() => setFreeSlots([]))
       .finally(() => setSlotsLoading(false));
  }, [clinicianId, bookDate, selectedService?.duration_min]);

  const submit = async () => {
    setErr('');
    if (completeness && !completeness.complete) {
      setErr('Please complete your profile before booking.');
      // AppLayout will pop the modal because completeness.complete is false.
      return;
    }
    if ((settings?.service_disciplines || ['physiotherapy']).filter(d => d !== 'massage_therapy').length >= 2 && !discipline) {
      setErr('Please choose which service you are looking for.'); return;
    }
    // Iter 72 — massage discipline requires a completed Part-1 intake first.
    if (discipline === 'massage_therapy' && massageIntakeStatus && !massageIntakeStatus.complete) {
      setShowMassageGate(true); return;
    }
    if (!startsAt) { setErr('Please choose a date and time.'); return; }
    // Iter 136 — no backdated bookings (server enforces this too).
    if (startsAt.slice(0, 16) < localInputValue()) {
      setErr('That time is in the past — please pick a future date and time.'); return;
    }
    if (needsIntake) { setErr('Please complete your patient interview form first.'); return; }
    if (isReturning && !reason.trim()) {
      setErr('Please describe what is bringing you in today.'); return;
    }
    setLoading(true);
    try {
      const body = {
        starts_at: startsAt,
        service_id: serviceId || undefined,
        clinician_id: clinicianId || undefined,
        location_id: locationId || undefined,
        notes: notes || undefined,
        reason_for_visit: reason.trim() || undefined,
        new_condition: !!newCondition,
        discipline: discipline || undefined,
      };
      await api.post('/appointments/self', body);
      // Show prep instructions before redirecting
      try {
        const { data } = await api.get('/booking/prep-instructions', { params: { locale: 'en' } });
        setPrep(data);
      } catch {
        navigate('/patient/appointments');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns a structured 412 with {code:'profile_incomplete', missing:[...]}
      if (e.response?.status === 412 && detail && typeof detail === 'object' && detail.code === 'profile_incomplete') {
        setErr(detail.message || 'Please complete your profile before booking.');
        loadCompleteness();  // AppLayout will re-pop the modal on next render.
      } else {
        setErr(typeof detail === 'string' ? detail : (detail?.message || e.message));
      }
    } finally { setLoading(false); }
  };

  const [prep, setPrep] = useState(null);

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      {/* Iter 72 — Massage intake gate. Patient must complete the SOAP-style
          Part-1 (Subjective) before they can book a massage appointment. */}
      {showMassageGate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="massage-gate-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-[#FFB000] mt-0.5 flex-shrink-0"/>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-[#A86F31] font-semibold mb-1">MASSAGE INTAKE REQUIRED</div>
                <h2 className="text-xl font-heading font-bold">Complete your massage intake first</h2>
              </div>
            </div>
            <p className="text-sm text-fg-secondary mb-2">
              Before booking a massage session we need you to complete <strong>Part 1 — Subjective</strong> so your therapist
              can prepare:
            </p>
            <ul className="text-sm text-fg-secondary list-disc list-inside space-y-1 mb-4 ml-1">
              <li>Reason for the visit · pain scale · goals</li>
              <li>Preferred pressure & areas to avoid</li>
              <li>Brief safety screen (allergies, medications, contraindications)</li>
              <li>Consent + signature</li>
            </ul>
            <div className="text-xs text-fg-muted mb-4 italic">Takes about 3 minutes — you only do this once.</div>
            <div className="flex gap-2 flex-wrap justify-end">
              <button onClick={()=>setShowMassageGate(false)}
                      className="btn-secondary text-sm" data-testid="massage-gate-cancel">
                Not now
              </button>
              <button onClick={()=>navigate('/patient/massage-intake')}
                      className="btn-primary text-sm inline-flex items-center gap-1" data-testid="massage-gate-go">
                Complete intake now <ChevronRight className="w-4 h-4"/>
              </button>
            </div>
          </div>
        </div>
      )}
      {prep && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="prep-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-2">BOOKING CONFIRMED</div>
            <h2 className="text-2xl font-heading font-bold mb-3">{prep.title}</h2>
            <ul className="space-y-2 mb-5">
              {prep.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-fg-secondary">
                  <span className="text-brand mt-0.5">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <button onClick={()=>navigate('/patient/appointments')}
                    className="btn-primary w-full" data-testid="prep-close">
              Got it — see my appointments
            </button>
          </div>
        </div>
      )}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">SELF-SCHEDULE</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Book an appointment</h1>
        <p className="text-fg-muted mt-2 text-sm">Pick your clinician, a service and a time. {prepayPercent}% prepayment confirms your slot.</p>
      </div>

      {completeness && !completeness.complete ? (
        <div className="bento-card flex items-start gap-3 border-[#B83A3A] border-l-4" data-testid="profile-gate">
          <AlertCircle className="w-5 h-5 text-[#B83A3A] mt-0.5 flex-shrink-0"/>
          <div className="flex-1">
            <div className="font-heading font-semibold text-lg">Complete your profile to book</div>
            <div className="text-sm text-fg-secondary mt-1.5">
              We need these details on file before your first appointment so the clinic can prepare your record and meet
              EU/HIPAA medical-record requirements. Takes ~2 minutes — you only do this once.
            </div>
            <div className="text-xs text-fg-muted mt-2">
              <strong>Still missing:</strong> {completeness.missing.map(k => REQUIRED_FIELD_LABELS[k] || k).join(' · ')}
            </div>
            <p className="text-xs text-fg-muted mt-3 italic">The completion popup is open on the right — fill it in and you&apos;re done.</p>
          </div>
        </div>
      ) : needsIntake ? (
        <div className="bento-card flex items-start gap-3 border-[#FFB000] border-l-4" data-testid="intake-warn">
          <AlertCircle className="w-5 h-5 text-[#FFB000] mt-0.5 flex-shrink-0"/>
          <div className="flex-1">
            <div className="font-heading font-semibold text-lg">Patient intake required</div>
            <div className="text-sm text-fg-secondary mt-1.5">
              Before your first appointment we need information of your present health condition and medical history.
              This lets your therapist arrive prepared, and screen for red flags and any contraindications.
            </div>
            <div className="text-xs text-fg-muted mt-2">Takes about 5 minutes · all data is encrypted and stored under GDPR.</div>
            <button onClick={()=>navigate('/patient/interview')} className="btn-primary text-sm mt-4 inline-flex items-center gap-1" data-testid="go-intake">
              Complete intake form <ChevronRight className="w-4 h-4"/>
            </button>
          </div>
        </div>
      ) : (
      <div className="bento-card">
        {/* Iter 130 — Pre-fill transparency banner. Tells the patient which
            fields were auto-populated + from where, and gives them a 1-tap
            "Start blank" reset. Auto-hides for first-timers with no history. */}
        {prefillSummary && (
          <div
            className="mb-4 p-3 rounded-lg border border-brand/30 bg-brand/5 flex items-start gap-3"
            data-testid="prefill-banner"
          >
            <Sparkles className="w-4 h-4 text-brand mt-0.5 shrink-0"/>
            <div className="flex-1 text-xs text-fg-secondary leading-relaxed">
              <div className="font-medium text-fg">
                Pre-filled from your {prefillSummary.source === 'last_visit' ? 'last visit' : 'answers on our website'}
                {prefillSummary.seedDate && (
                  <span className="text-fg-muted font-normal">
                    {' '}· {new Date(prefillSummary.seedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
              <div className="text-fg-muted mt-0.5">
                We set the {prefillSummary.fields.join(' · ')} field{prefillSummary.fields.length === 1 ? '' : 's'} for you.
                Change anything you like — the date & time are always fresh.
              </div>
            </div>
            <button
              type="button"
              onClick={clearPrefill}
              className="text-[11px] uppercase tracking-wider font-semibold text-brand hover:text-fg inline-flex items-center gap-1 shrink-0"
              data-testid="prefill-clear"
            >
              <X className="w-3 h-3"/> Start blank
            </button>
          </div>
        )}
        {/* Discipline picker — only shown when the clinic offers 2+ disciplines (Wave 2).
            Solo-discipline tenants auto-select theirs and skip this step.
            Iter 133 — Massage therapy is filtered OUT here: it now lives on
            the dedicated Massage Assessment/Booking flow (accessible when
            the massage_intake addon is activated). Osteopathy is retained. */}
        {(settings?.service_disciplines || ['physiotherapy']).filter(d => d !== 'massage_therapy').length >= 2 && (
          <Step icon={ShieldCheck} label="What service are you looking for?">
            <div className="grid sm:grid-cols-3 gap-2" data-testid="discipline-picker">
              {(settings?.service_disciplines || [])
                .filter(d => d !== 'massage_therapy')
                .map(d => {
                const meta = {
                  physiotherapy: { label: 'Physiotherapy', sub: 'Movement, rehab, manual therapy' },
                  osteopathy:    { label: 'Osteopathy',    sub: 'Whole-body manual treatment' },
                  chiropractic:  { label: 'Chiropractic',  sub: 'Spinal/joint adjustment' },
                }[d] || { label: d, sub: '' };
                const active = discipline === d;
                return (
                  <button key={d} type="button" onClick={()=>{ setDiscipline(d); }}
                          className={`p-3 rounded-lg border text-left transition ${active ? 'border-brand ring-2 ring-brand/20 bg-brand/5' : 'border-soft hover:border-fg/30'}`}
                          data-testid={`disc-pick-${d}`}>
                    <div className="text-sm font-medium">{meta.label}</div>
                    <div className="text-[11px] text-fg-muted mt-0.5">{meta.sub}</div>
                  </button>
                );
              })}
            </div>
          </Step>
        )}

        <Step icon={Briefcase} label="Service">
          <select className="input" value={serviceId} onChange={e=>{setServiceId(e.target.value); const s=services.find(x=>x.service_id===e.target.value); if(s) setCurrency(s.currency);}} data-testid="self-service">
            <option value="">Select a service…</option>
            {services
              .filter(s => s.discipline !== 'massage_therapy')  // Iter 133 — massage lives on its own flow
              .filter(s => !discipline || !s.discipline || s.discipline === discipline)
              .map(s => <option key={s.service_id} value={s.service_id}>{`${s.name} — ${s.price} ${s.currency} · ${s.duration_min}m`}</option>)}
          </select>
        </Step>

        <Step icon={User} label="Clinician">
          <select className="input" value={clinicianId} onChange={e=>setClinicianId(e.target.value)} data-testid="self-clinician">
            <option value="">No preference</option>
            {clinicians.map(c => <option key={c.user_id} value={c.user_id}>{c.name}{c.credentials?`, ${c.credentials}`:''}</option>)}
          </select>
        </Step>

        {/* Branch picker — only meaningful with 2+ locations. Solo tenants
            (zero or one location) skip this step entirely. */}
        {locations.length >= 2 && (
          <Step icon={MapPin} label="Which clinic would you like to visit?">
            <div className="grid sm:grid-cols-2 gap-2" data-testid="branch-picker">
              {locations.map(loc => {
                const active = locationId === loc.location_id;
                return (
                  <button key={loc.location_id} type="button"
                          onClick={() => setLocationId(loc.location_id)}
                          className={`text-left p-3 rounded-lg border-2 transition ${active ? 'border-brand ring-2 ring-brand/15 bg-brand/5' : 'border-soft hover:border-brand/40'}`}
                          data-testid={`branch-${loc.location_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading font-semibold text-sm inline-flex items-center gap-1.5">
                        {loc.name}
                        {loc.is_primary && <Star className="w-3 h-3 fill-[#FFB000] text-[#FFB000]" title="Main branch"/>}
                      </div>
                      {active && <ShieldCheck className="w-4 h-4 text-brand shrink-0"/>}
                    </div>
                    {loc.address && <div className="text-[11px] text-fg-muted mt-1 leading-relaxed">{loc.address}</div>}
                    {loc.hours   && <div className="text-[10px] text-fg-muted mt-1 uppercase tracking-wider">{loc.hours}</div>}
                  </button>
                );
              })}
            </div>
          </Step>
        )}

        <Step icon={Calendar} label="Date & time">
          {!clinicianId ? (
            <input type="datetime-local" className="input" value={startsAt} onChange={e=>setStartsAt(e.target.value)}
                   min={localInputValue()} data-testid="self-time"/>
          ) : (
            <div>
              <DateDropdownPicker value={bookDate} onChange={setBookDate}
                                  minDate={localDateStr()} testId="self-date"/>
              {bookDate && (
                <div className="mt-3" data-testid="slot-grid">
                  {slotsLoading && <div className="text-xs text-fg-muted">Loading slots…</div>}
                  {!slotsLoading && freeSlots.length === 0 && (
                    <div className="mt-2 surface border border-soft rounded-lg p-3" data-testid="no-slots-waitlist-offer">
                      <div className="text-xs text-fg-muted">
                        No available slots that day. Try another date — or join the waitlist and we&apos;ll email you the moment a slot opens up.
                      </div>
                      <button type="button" onClick={joinWaitlist} disabled={waitlistJoining || waitlistJoined}
                              className="btn-secondary text-xs mt-2 inline-flex items-center gap-2" data-testid="join-waitlist-btn">
                        {waitlistJoined ? '✓ On the waitlist' : (waitlistJoining ? 'Joining…' : 'Add me to the waitlist')}
                      </button>
                    </div>
                  )}
                  {!slotsLoading && freeSlots.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                      {freeSlots.map(slot => {
                        const label = wallTime(slot, settings?.clock_format || '24h');
                        const active = startsAt === slot;
                        return (
                          <button key={slot} type="button"
                                  onClick={()=>setStartsAt(slot)}
                                  className={`text-xs py-2 rounded border transition ${active
                                    ? 'bg-brand text-white border-brand'
                                    : 'border-soft hover:border-brand'}`}
                                  data-testid={`slot-${wallTime(slot).replace(':','')}`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {!bookDate && (
                <div className="text-[11px] text-fg-muted mt-1">Pick a date to see this clinician&apos;s free slots.</div>
              )}
            </div>
          )}
        </Step>

        <Step label="Notes (optional)">
          <textarea className="input min-h-[60px]" value={notes} onChange={e=>setNotes(e.target.value)} data-testid="self-notes" placeholder="Anything your clinician should know?"/>
        </Step>

        {isReturning && (
          <Step label="What's bringing you in today? *">
            <textarea className="input min-h-[55px]"
                      value={reason}
                      onChange={e=>setReason(e.target.value)}
                      placeholder={newCondition
                        ? "Describe the new issue — when it started, what hurts, anything that triggered it…"
                        : "Continuing care for the same condition, or a quick check-in? Describe in one or two sentences."}
                      data-testid="self-reason"/>
            <label className="mt-2 flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={newCondition} onChange={e=>setNewCondition(e.target.checked)}
                     className="mt-0.5 accent-[var(--brand)]"
                     data-testid="self-new-condition"/>
              <span>
                <span className="font-medium">This is a new condition</span>
                <span className="text-fg-muted text-xs"> · check this if it&apos;s unrelated to your previous treatment so your therapist can prepare a fresh assessment.</span>
              </span>
            </label>
          </Step>
        )}

        {selectedService && (
          <>
            <div className="surface border border-soft rounded-lg p-3 mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-fg-muted">Total · session price</div>
                  <div className="font-heading font-bold text-2xl mt-0.5">
                    {selectedService.price.toFixed(2)} <span className="text-sm font-normal text-fg-muted">{currency || selectedService.currency}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-fg-muted">Prepay {prepayPercent}%</div>
                  <div className="font-heading font-bold text-lg mt-0.5">
                    {(selectedService.price * prepayPercent / 100).toFixed(2)} <span className="text-xs font-normal text-fg-muted">{currency || selectedService.currency}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-[var(--accent-soft)] text-[#8A5A00]"
                   data-testid="vat-exempt-badge"
                   title="Physiotherapy services are exempt from VAT under Polish VAT Act art. 43(1)(19)">
                <ShieldCheck className="w-3 h-3"/> Tax-exempt · medical service
              </div>
              <CurrencyPreview amount={selectedService.price * prepayPercent / 100}
                               currency={currency || selectedService.currency} apptId="book"/>
              {settings?.prepay_enabled && (settings?.unpaid_release_minutes ?? 30) > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-[#A86F31] bg-[#FFB000]/10 border border-[#FFB000]/30 rounded-md px-2.5 py-1.5"
                     data-testid="book-unpaid-release-warning">
                  <span className="font-bold">⏱</span>
                  <span><strong>Heads up:</strong> pay the deposit within <strong>{settings?.unpaid_release_minutes ?? 30} minutes</strong> of booking — unpaid slots are released automatically and offered to the waitlist.</span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-soft text-[11px] text-fg-muted leading-relaxed flex items-start gap-1.5" data-testid="cancellation-policy">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-warn"/>
                <span>
                  <strong className="text-fg">Cancellation grace period:</strong> appointments can be cancelled free of charge up to <strong>{settings?.cancellation_hours ?? 24} hours before</strong> your scheduled time. Late cancellations or no-shows may forfeit the prepay.
                </span>
              </div>
            </div>
          </>
        )}

        {err && <div className="text-sm text-[#B83A3A] mt-3" data-testid="self-err">{err}</div>}

        <div className="mt-4 flex justify-end">
          <button onClick={submit} disabled={loading || needsIntake} className="btn-primary inline-flex items-center gap-2" data-testid="self-book-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
            Confirm booking
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

function Step({ icon: Icon, label, children }) {
  return (
    <label className="block mb-4">
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-brand"/>}
        <span className="text-xs text-fg-muted font-medium">{label}</span>
      </div>
      {children}
    </label>
  );
}
